import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "./form-field";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { DesignNodeLabel } from "@/service-hooks/use-create-design-node-service";

import {
  useGetFunctionalGraphService,
  ScenarioItem,
} from "@/service-hooks/use-get-functional-graph-service";
import { UnmappedScenario, useGetUnmappedScenariosService } from "@/service-hooks/use-get-unmapped-scenarios-service";
import { Pagination } from "./pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const SCENARIO_PAGE_SIZE = 10;

interface DesignNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>) => void;
  node: any;
  label: DesignNodeLabel | "";
  projectUuid?: string;
  isReadOnly?: boolean;
  defaultScenarioId?: string;
}

// Sentinel used because shadcn Select cannot use an empty-string item value
const NO_SCENARIO = "__none__";

const FLOW_MODALITIES = [
  "WEB",
  "MOBILE",
  "TABLET",
  "DESKTOP",
  "VOICE",
  "API",
  "KIOSK",
  "WATCH",
  "TV",
];

const PAGE_TYPES = ["LIST", "DETAIL", "FORM", "DASHBOARD"];

const COMPONENT_TYPES = ["ORGANISM", "MOLECULE", "ATOM", "TEMPLATE"];

const labelTitles: Record<DesignNodeLabel, string> = {
  UserJourney: "User Journey",
  Flow: "Flow",
  Page: "Page",
  Component: "Component",
};

export function DesignNodeDialog({
  isOpen,
  onClose,
  onSubmit,
  node,
  label,
  projectUuid = "",
  isReadOnly = false,
  defaultScenarioId,
}: DesignNodeDialogProps) {
  // Common fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // UserJourney field — scenario mapping
  const [scenarioId, setScenarioId] = useState("");
  const [scenarioPage, setScenarioPage] = useState(1);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [editScenarioMode, setEditScenarioMode] = useState(false);
  const [confirmRemapOpen, setConfirmRemapOpen] = useState(false);
  const scenarioDropdownRef = useRef<HTMLDivElement | null>(null);

  // Flow fields
  const [modality, setModality] = useState("");
  const [entryPoint, setEntryPoint] = useState("");
  const [exitPoint, setExitPoint] = useState("");

  // Page fields
  const [pageType, setPageType] = useState("");
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState("");

  // Component fields
  const [componentType, setComponentType] = useState("");
  const [designSystemRef, setDesignSystemRef] = useState("");
  const [props, setProps] = useState("");
  const [states, setStates] = useState("");
  const [layoutType, setLayoutType] = useState("");
  const [slots, setSlots] = useState("");

  const isEdit = !!node;
  const title = label
    ? `${isReadOnly ? "View" : isEdit ? "Edit" : "Create"} ${labelTitles[label as DesignNodeLabel] || label}`
    : "";

  const isUserJourney = label === "UserJourney";

  const mappedScenarioId: string = node?.scenarioId || defaultScenarioId || "";

  // Fetch scenarios not yet mapped to any user journey
  const {
    data: unmappedData,
    isLoading: scenariosLoading,
    isFetching: scenariosFetching,
  } = useGetUnmappedScenariosService({
    projectUuid,
    page: scenarioPage,
    limit: SCENARIO_PAGE_SIZE,
    enabled: isOpen && isUserJourney && !!projectUuid,
  });

  const unmappedTotal = unmappedData?.total ?? 0;
  const scenariosTotalPages = Math.max(
    1,
    Math.ceil(unmappedTotal / SCENARIO_PAGE_SIZE),
  );

  // The currently mapped scenario is excluded from the unmapped list, so fetch
  // it by id to show its real name instead of a generic fallback label.
  const { data: mappedScenarioData } = useGetFunctionalGraphService({
    projectUuid,
    label: "Scenario",
    filters: { id: mappedScenarioId },
    limit: 1,
    enabled: isOpen && isUserJourney && !!projectUuid && !!mappedScenarioId,
  });

  // Build the dropdown options: unmapped scenarios + the currently mapped one
  // (only prepend the mapped scenario on the first page to avoid duplication)
  const scenarioOptions = useMemo<UnmappedScenario[]>(() => {
    const options = [...(unmappedData?.data ?? [])];
    if (
      scenarioPage === 1 &&
      mappedScenarioId &&
      !options.some((s) => s.id === mappedScenarioId)
    ) {
      const mapped = mappedScenarioData?.data?.find(
        (s): s is ScenarioItem =>
          (s as ScenarioItem).id === mappedScenarioId &&
          "scenario" in s,
      );
      options.unshift({
        id: mappedScenarioId,
        scenario:
          mapped?.scenario ||
          node?.scenario ||
          node?.scenarioName ||
          "Currently mapped scenario",
      });
    }
    return options;
  }, [unmappedData, mappedScenarioData, mappedScenarioId, node, scenarioPage]);

  // Initialize / reset state when dialog opens
  useEffect(() => {
    if (!isOpen) return;
    setName(node?.name || "");
    setDescription(node?.description || "");

    setScenarioId(node?.scenarioId || defaultScenarioId || "");
    setScenarioPage(1);
    setScenarioOpen(false);
    setEditScenarioMode(false);
    setConfirmRemapOpen(false);

    setModality(node?.modality || "");
    setEntryPoint(node?.entryPoint || "");
    setExitPoint(node?.exitPoint || "");

    setPageType(node?.pageType || "");
    setRequiresAuth(!!node?.requiresAuth);
    setAllowedRoles(
      Array.isArray(node?.allowedRoles) ? node.allowedRoles.join(", ") : "",
    );

    setComponentType(node?.type || "");
    setDesignSystemRef(node?.designSystemRef || "");
    setProps(node?.props || "");
    setStates(Array.isArray(node?.states) ? node.states.join(", ") : "");
    setLayoutType(node?.layoutType || "");
    setSlots(Array.isArray(node?.slots) ? node.slots.join(", ") : "");
  }, [isOpen, node]);

  // Click-outside handler for the custom scenario dropdown
  useEffect(() => {
    if (!scenarioOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        scenarioDropdownRef.current &&
        !scenarioDropdownRef.current.contains(e.target as Node)
      ) {
        setScenarioOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [scenarioOpen]);

  const selectedScenarioLabel = useMemo(() => {
    if (!scenarioId) return "";
    const fromOptions = scenarioOptions.find((s) => s.id === scenarioId);
    if (fromOptions) return fromOptions.scenario;
    return (
      node?.scenario || node?.scenarioName || "Currently mapped scenario"
    );
  }, [scenarioId, scenarioOptions, node]);

  const csvToArray = (val: string): string[] =>
    val
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

  const handleSubmit = () => {
    if (!name.trim()) return;

    const data: Record<string, any> = {};

    const trimmedName = name.trim();
    if (!isEdit || trimmedName !== (node?.name || "")) {
      data.name = trimmedName;
    }

    const trimmedDescription = description.trim();
    if (isEdit) {
      if (trimmedDescription !== (node?.description || "")) {
        data.description = trimmedDescription;
      }
    } else if (trimmedDescription) {
      data.description = trimmedDescription;
    }

    if (label === "UserJourney") {
      const newScenarioId =
        scenarioId && scenarioId !== NO_SCENARIO ? scenarioId : "";
      const originalScenarioId = node?.scenarioId || "";
      // Only send scenarioId when it actually changed — otherwise an untouched
      // dropdown would overwrite the existing journey ↔ scenario mapping.
      if (!isEdit || newScenarioId !== originalScenarioId) {
        data.scenarioId = newScenarioId;
      }
    } else if (label === "Flow") {
      if (!isEdit || modality !== (node?.modality || "")) {
        data.modality = modality;
      }
      const trimmedEntry = entryPoint.trim();
      if (isEdit) {
        if (trimmedEntry !== (node?.entryPoint || "")) {
          data.entryPoint = trimmedEntry;
        }
      } else if (trimmedEntry) {
        data.entryPoint = trimmedEntry;
      }
      const trimmedExit = exitPoint.trim();
      if (isEdit) {
        if (trimmedExit !== (node?.exitPoint || "")) {
          data.exitPoint = trimmedExit;
        }
      } else if (trimmedExit) {
        data.exitPoint = trimmedExit;
      }
    } else if (label === "Page") {
      if (isEdit) {
        if (pageType !== (node?.pageType || "")) data.pageType = pageType;
        if (requiresAuth !== !!node?.requiresAuth) data.requiresAuth = requiresAuth;
        const roles = csvToArray(allowedRoles);
        const originalRoles = Array.isArray(node?.allowedRoles)
          ? node.allowedRoles
          : [];
        if (roles.join(",") !== originalRoles.join(",")) {
          data.allowedRoles = roles;
        }
      } else {
        if (pageType) data.pageType = pageType;
        data.requiresAuth = requiresAuth;
        const roles = csvToArray(allowedRoles);
        if (roles.length) data.allowedRoles = roles;
      }
    } else if (label === "Component") {
      if (isEdit) {
        if (componentType !== (node?.type || "")) data.type = componentType;
        const trimmedRef = designSystemRef.trim();
        if (trimmedRef !== (node?.designSystemRef || "")) {
          data.designSystemRef = trimmedRef;
        }
        const trimmedProps = props.trim();
        if (trimmedProps !== (node?.props || "")) {
          data.props = trimmedProps;
        }
        const stateArr = csvToArray(states);
        const originalStates = Array.isArray(node?.states) ? node.states : [];
        if (stateArr.join(",") !== originalStates.join(",")) {
          data.states = stateArr;
        }
        const trimmedLayout = layoutType.trim();
        if (trimmedLayout !== (node?.layoutType || "")) {
          data.layoutType = trimmedLayout;
        }
        const slotArr = csvToArray(slots);
        const originalSlots = Array.isArray(node?.slots) ? node.slots : [];
        if (slotArr.join(",") !== originalSlots.join(",")) {
          data.slots = slotArr;
        }
      } else {
        data.type = componentType;
        if (designSystemRef.trim()) data.designSystemRef = designSystemRef.trim();
        if (props.trim()) data.props = props.trim();
        const stateArr = csvToArray(states);
        if (stateArr.length) data.states = stateArr;
        if (layoutType.trim()) data.layoutType = layoutType.trim();
        const slotArr = csvToArray(slots);
        if (slotArr.length) data.slots = slotArr;
      }
    }

    if (isEdit && Object.keys(data).length === 0) return;

    onSubmit(data);
  };

  const isValid = (() => {
    if (!name.trim()) return false;
    if (label === "Flow" && !modality) return false;
    if (label === "Component" && !componentType) return false;
    return true;
  })();

  const hasChanges = (() => {
    if (!isEdit) return true;

    if ((node?.name || "") !== name.trim()) return true;
    if ((node?.description || "") !== description.trim()) return true;

    if (label === "UserJourney") {
      const originalScenarioId = node?.scenarioId || "";
      const newScenarioId =
        scenarioId && scenarioId !== NO_SCENARIO ? scenarioId : "";
      if (originalScenarioId !== newScenarioId) return true;
    } else if (label === "Flow") {
      if ((node?.modality || "") !== modality) return true;
      if ((node?.entryPoint || "") !== entryPoint.trim()) return true;
      if ((node?.exitPoint || "") !== exitPoint.trim()) return true;
    } else if (label === "Page") {
      if ((node?.pageType || "") !== pageType) return true;
      if (!!node?.requiresAuth !== requiresAuth) return true;
      const originalRoles = Array.isArray(node?.allowedRoles)
        ? node.allowedRoles.join(", ")
        : "";
      if (originalRoles !== allowedRoles) return true;
    } else if (label === "Component") {
      if ((node?.type || "") !== componentType) return true;
      if ((node?.designSystemRef || "") !== designSystemRef.trim()) return true;
      if ((node?.props || "") !== props.trim()) return true;
      const originalStates = Array.isArray(node?.states)
        ? node.states.join(", ")
        : "";
      if (originalStates !== states) return true;
      if ((node?.layoutType || "") !== layoutType.trim()) return true;
      const originalSlots = Array.isArray(node?.slots)
        ? node.slots.join(", ")
        : "";
      if (originalSlots !== slots) return true;
    }

    return false;
  })();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-surface-card border-surface-border text-text-primary max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Fill in the fields to configure this design node.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <FormField label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name"
              className="bg-surface-input border-surface-border text-text-primary"
              readOnly={isReadOnly}
            />
          </FormField>

          <FormField label="Description">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              className="bg-surface-input border border-surface-border text-text-primary"
              readOnly={isReadOnly}
            />
          </FormField>

          {isUserJourney && (
            <FormField label="Scenario">
              <div ref={scenarioDropdownRef}>
                {(() => {
                  const scenarioLocked =
                    isReadOnly || (!!mappedScenarioId && !editScenarioMode);
                  const triggerButton = (
                    <button
                      type="button"
                      disabled={scenariosLoading || scenarioLocked}
                      onClick={() => setScenarioOpen((o) => !o)}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-surface-border bg-surface-input px-3 py-2 text-sm text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span
                        className={`truncate ${selectedScenarioLabel ? "" : "text-muted"}`}
                      >
                        {scenariosLoading
                          ? "Loading scenarios..."
                          : selectedScenarioLabel || "Select a scenario"}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 opacity-70 transition-transform ${
                          scenarioOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  );
                  return scenarioLocked ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block w-full cursor-not-allowed">
                            <span className="pointer-events-none block w-full">
                              {triggerButton}
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="bg-surface-hover text-white px-2 py-1 text-xs rounded"
                        >
                          This user journey is already mapped to a scenario
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    triggerButton
                  );
                })()}

                {mappedScenarioId && !isReadOnly && (
                  <div className="mt-1 flex items-center justify-between text-xs text-muted">
                    {editScenarioMode ? (
                      <>
                        <span>Selecting a new scenario will replace the current mapping.</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditScenarioMode(false);
                            setScenarioId(mappedScenarioId);
                            setScenarioOpen(false);
                          }}
                          className="ml-2 text-primary hover:underline"
                        >
                          Keep current
                        </button>
                      </>
                    ) : (
                      <>
                        <span>Mapped scenario is locked.</span>
                        <button
                          type="button"
                          onClick={() => setConfirmRemapOpen(true)}
                          className="ml-2 text-primary hover:underline"
                        >
                          Change
                        </button>
                      </>
                    )}
                  </div>
                )}

                {scenarioOpen && (
                  <div className="mt-1 rounded-md border border-surface-border bg-surface-card shadow-lg">
                    <div className="relative max-h-64 overflow-y-auto py-1">
                      {scenarioOptions.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            setScenarioId(s.id);
                            setScenarioOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-hover ${
                            scenarioId === s.id ? "bg-surface-active" : ""
                          }`}
                        >
                          {s.scenario}
                        </button>
                      ))}
                      {scenariosFetching && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-surface-card/70">
                          {/* <Loader2 className="h-5 w-5 animate-spin text-text-primary" /> */}
                        </div>
                      )}
                    </div>
                    <div className="border-t border-surface-border">
                      <Pagination
                        currentItems={unmappedData?.data?.length ?? 0}
                        totalItems={unmappedTotal}
                        currentPage={scenarioPage}
                        totalPages={scenariosTotalPages}
                        isLoading={scenariosFetching}
                        onPrevious={() =>
                          setScenarioPage((p) => Math.max(1, p - 1))
                        }
                        onNext={() =>
                          setScenarioPage((p) =>
                            Math.min(scenariosTotalPages, p + 1),
                          )
                        }
                        canGoPrevious={scenarioPage > 1}
                        canGoNext={scenarioPage < scenariosTotalPages}
                        itemName="scenarios"
                        showPageNumbers={true}
                        mode="pages"
                      />
                    </div>
                  </div>
                )}
              </div>
            </FormField>
          )}

          {label === "Flow" && (
            <>
              <FormField label="Modality" required>
                <Select value={modality} onValueChange={setModality} disabled={isReadOnly}>
                  <SelectTrigger className="bg-surface-input border-surface-border text-text-primary">
                    <SelectValue placeholder="Select modality" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLOW_MODALITIES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Entry Point (page id)">
                <Input
                  value={entryPoint}
                  onChange={(e) => setEntryPoint(e.target.value)}
                  placeholder="Optional starting page id"
                  className="bg-surface-input border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>

              <FormField label="Exit Point (page id)">
                <Input
                  value={exitPoint}
                  onChange={(e) => setExitPoint(e.target.value)}
                  placeholder="Optional ending page id"
                  className="bg-surface-input border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>
            </>
          )}

          {label === "Page" && (
            <>
              <FormField label="Page Type">
                <Select value={pageType} onValueChange={setPageType} disabled={isReadOnly}>
                  <SelectTrigger className="bg-surface-input border-surface-border text-text-primary">
                    <SelectValue placeholder="Select page type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <label className={`flex items-center gap-2 ${isReadOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
                <input
                  type="checkbox"
                  checked={requiresAuth}
                  onChange={(e) => setRequiresAuth(e.target.checked)}
                  disabled={isReadOnly}
                  className="h-4 w-4 rounded border-surface-border accent-primary"
                />
                <span className="text-sm text-text-primary">
                  Requires Authentication
                </span>
              </label>

              <FormField label="Allowed Roles (comma-separated)">
                <Input
                  value={allowedRoles}
                  onChange={(e) => setAllowedRoles(e.target.value)}
                  placeholder="admin, user"
                  className="bg-surface-input border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>
            </>
          )}

          {label === "Component" && (
            <>
              <FormField label="Type" required>
                <Select value={componentType} onValueChange={setComponentType} disabled={isReadOnly}>
                  <SelectTrigger className="bg-surface-input border-surface-border text-text-primary">
                    <SelectValue placeholder="Select component type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPONENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Design System Ref">
                <Input
                  value={designSystemRef}
                  onChange={(e) => setDesignSystemRef(e.target.value)}
                  placeholder="Reference to design system"
                  className="bg-surface-input border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>

              <FormField label="Props (JSON)">
                <Textarea
                  value={props}
                  onChange={(e) => setProps(e.target.value)}
                  placeholder='{"key": "value"}'
                  className="bg-surface-input border border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>

              <FormField label="States (comma-separated)">
                <Input
                  value={states}
                  onChange={(e) => setStates(e.target.value)}
                  placeholder="default, hover, active"
                  className="bg-surface-input border-surface-border text-text-primary"
                  readOnly={isReadOnly}
                />
              </FormField>

              {componentType === "TEMPLATE" && (
                <>
                  <FormField label="Layout Type">
                    <Input
                      value={layoutType}
                      onChange={(e) => setLayoutType(e.target.value)}
                      placeholder="GRID, FLEX, SIDEBAR, FULL"
                      className="bg-surface-input border-surface-border text-text-primary"
                      readOnly={isReadOnly}
                    />
                  </FormField>

                  <FormField label="Slots (comma-separated)">
                    <Input
                      value={slots}
                      onChange={(e) => setSlots(e.target.value)}
                      placeholder="header, content, footer"
                      className="bg-surface-input border-surface-border text-text-primary"
                      readOnly={isReadOnly}
                    />
                  </FormField>
                </>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-surface-border text-white"
          >
            {isReadOnly ? "Close" : "Cancel"}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSubmit} disabled={!isValid || !hasChanges}>
              {isEdit ? "Update" : "Create"}
            </Button>
          )}
        </div>

        <AlertDialog
          open={confirmRemapOpen}
          onOpenChange={setConfirmRemapOpen}
        >
          <AlertDialogContent className="bg-surface-card border-surface-border text-text-primary">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Replace current scenario mapping?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-muted">
                Selecting a new scenario will remove the existing mapping for
                this user journey.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-surface-hover text-text-primary border-surface-border">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => setEditScenarioMode(true)}
                className="bg-primary text-button-text hover:bg-primary-dark"
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
